import React, { createContext, useContext, useState } from "react";
import { useSupportTickets } from "../hooks/useSupportTickets";
import { useSupportUnreadContext } from "./SupportUnreadContext";

interface SupportContextType {
  isChatOpen: boolean;
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;
  unreadCount: number;
  selectedTicketId: string | null;
  setSelectedTicketId: (id: string | null) => void;
  tickets: any[];
  loading: boolean;
  createTicket: () => Promise<string | null>;
  closeTicket: (ticketId: string) => Promise<void>;
  reopenTicket: (ticketId: string) => Promise<void>;
}

const SupportContext = createContext<SupportContextType | null>(null);

export const SupportProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const {
    tickets,
    loading,
    selectedTicketId,
    setSelectedTicketId,
    createTicket,
    closeTicket,
    reopenTicket,
    getTotalUnreadCount,
  } = useSupportTickets();

  const openChat = () => setIsChatOpen(true);
  const closeChat = () => setIsChatOpen(false);
  const toggleChat = () => setIsChatOpen((prev) => !prev);

  const { unreadCount } = useSupportUnreadContext();

  return (
    <SupportContext.Provider
      value={{
        isChatOpen,
        openChat,
        closeChat,
        toggleChat,
        unreadCount,
        selectedTicketId,
        setSelectedTicketId,
        tickets,
        loading,
        createTicket,
        closeTicket,
        reopenTicket,
      }}
    >
      {children}
    </SupportContext.Provider>
  );
};

export const useSupport = (): SupportContextType => {
  const context = useContext(SupportContext);
  if (!context) {
    throw new Error("useSupport must be used within a SupportProvider");
  }
  return context;
};
